// src/pages/TeachersPage/components/TeacherManagement/TeacherManagement.jsx
import React, { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faUserPlus,
	faEdit,
	faShieldAlt,
	faUser,
	faEnvelope,
} from "@fortawesome/free-solid-svg-icons";

import {
	useGetTeachersQuery,
	useDeleteTeacherMutation,
} from "../../../../store/api/teacherApiSlice";
import { useGetSubjectsQuery } from "../../../../store/api/subjectApiSlice";
import { useGetDepartmentsQuery } from "../../../../store/api/departmentApiSlice";

import { Loading } from "../../../../components/common";
import TeacherModal from "../TeacherModal";
import styles from "./TeacherManagement.module.css";

const TeacherManagement = () => {
	// API queries
	const {
		data: teachers = [],
		isLoading: teachersLoading,
		error: teachersError,
	} = useGetTeachersQuery();

	const { data: subjects = [] } = useGetSubjectsQuery();
	const { data: departments = [] } = useGetDepartmentsQuery();

	// Modal state
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedTeacher, setSelectedTeacher] = useState(null);

	// Expanded teacher state
	const [expandedTeacher, setExpandedTeacher] = useState(null);

	// Delete mutation
	const [deleteTeacher] = useDeleteTeacherMutation();

	// Transform teacher data for display
	const transformedTeachers = useMemo(() => {
		return teachers.map((teacher) => {
			const transformedSubjects = [];

			if (teacher.assignedSubjects) {
				teacher.assignedSubjects.forEach((assignment) => {
					const subject = subjects.find((s) => s.id === assignment.subjectId);
					if (subject) {
						const classes = assignment.departmentId.map((depId) => {
							const department = departments.find((d) => d.id === depId);
							return department ? department.departmentName : depId;
						});

						transformedSubjects.push({
							id: subject.id,
							name: subject.name,
							classes: classes,
						});
					}
				});
			}

			return {
				...teacher, // Keep all original teacher properties
				name: `${teacher.firstName} ${teacher.lastName}`,
				subjects: transformedSubjects,
			};
		});
	}, [teachers, subjects, departments]);

	const countTotalClasses = (teacher) => {
		return teacher.subjects.reduce(
			(total, subject) => total + subject.classes.length,
			0
		);
	};

	const toggleExpand = (teacherId) => {
		setExpandedTeacher(expandedTeacher === teacherId ? null : teacherId);
	};

	const handleAddTeacher = () => {
		setSelectedTeacher(null);
		setIsModalOpen(true);
	};

	const handleEditTeacher = (transformedTeacher, e) => {
		e.stopPropagation();

		// Find the original teacher data and pass it to the modal
		// The modal expects the original structure with assignedSubjects
		const originalTeacher = teachers.find(
			(t) => t.id === transformedTeacher.id
		);
		setSelectedTeacher(originalTeacher);
		setIsModalOpen(true);
	};

	// Loading state
	if (teachersLoading) {
		return <Loading />;
	}

	// Error state
	if (teachersError) {
		return (
			<div className={styles.errorState}>
				<h2>Error Loading Teachers</h2>
				<p>Unable to load teacher data. Please try again.</p>
			</div>
		);
	}

	return (
		<div className={styles.teacherManagement}>
			{/* Header */}
			<div className={styles.header}>
				<div className={styles.headerLeft}>
					<h1 className={styles.title}>
						<FontAwesomeIcon icon={faUser} className={styles.titleIcon} />
						Teachers Management
					</h1>
					<div className={styles.stats}>
						<span className={styles.statPill}>
							<FontAwesomeIcon icon={faUser} />
							{teachers.length} Teachers
						</span>
						<span className={`${styles.statPill} ${styles.adminPill}`}>
							<FontAwesomeIcon icon={faShieldAlt} />
							{teachers.filter((t) => t.isAdmin).length} Admins
						</span>
					</div>
				</div>
				<div className={styles.headerRight}>
					<button className={styles.addButton} onClick={handleAddTeacher}>
						<FontAwesomeIcon icon={faUserPlus} />
						Add Teacher
					</button>
				</div>
			</div>

			{/* Teachers Table */}
			<div className={styles.tableContainer}>
				{transformedTeachers.length > 0 ? (
					<table className={styles.teachersTable}>
						<thead>
							<tr>
								<th>Teacher</th>
								<th>Email</th>
								<th>Subjects</th>
								<th>Classes</th>
								<th>Role</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{transformedTeachers.map((teacher) => (
								<React.Fragment key={teacher.id}>
									<tr
										className={`${styles.teacherRow} ${
											expandedTeacher === teacher.id ? styles.expanded : ""
										}`}
										onClick={() => toggleExpand(teacher.id)}
									>
										<td className={styles.nameCell}>
											<div className={styles.teacherInfo}>
												<div className={styles.teacherName}>{teacher.name}</div>
											</div>
										</td>
										<td className={styles.emailCell}>
											<FontAwesomeIcon
												icon={faEnvelope}
												className={styles.emailIcon}
											/>
											{teacher.email}
										</td>
										<td className={styles.countCell}>
											<span className={styles.countBadge}>
												{teacher.subjects.length}
											</span>
										</td>
										<td className={styles.countCell}>
											<span className={styles.countBadge}>
												{countTotalClasses(teacher)}
											</span>
										</td>
										<td className={styles.roleCell}>
											{teacher.isAdmin ? (
												<span className={styles.adminBadge}>
													<FontAwesomeIcon icon={faShieldAlt} />
													Admin
												</span>
											) : (
												<span className={styles.teacherBadge}>
													<FontAwesomeIcon icon={faUser} />
													Teacher
												</span>
											)}
										</td>
										<td className={styles.actionsCell}>
											<button
												className={styles.editButton}
												onClick={(e) => handleEditTeacher(teacher, e)}
											>
												<FontAwesomeIcon icon={faEdit} />
											</button>
										</td>
									</tr>

									{/* Expanded row showing subjects */}
									{expandedTeacher === teacher.id && (
										<tr className={styles.expandedRow}>
											<td colSpan="6">
												<div className={styles.subjectDetails}>
													<h4 className={styles.subjectDetailsHeader}>
														Assigned Subjects
													</h4>
													{teacher.subjects.length > 0 ? (
														<table className={styles.subjectsTable}>
															<thead>
																<tr>
																	<th>Subject</th>
																	<th>Classes</th>
																</tr>
															</thead>
															<tbody>
																{teacher.subjects.map((subject) => (
																	<tr key={subject.id}>
																		<td className={styles.subjectName}>
																			{subject.name}
																		</td>
																		<td className={styles.subjectClasses}>
																			<div className={styles.classChips}>
																				{subject.classes.map((cls) => (
																					<span
																						className={styles.classChip}
																						key={cls}
																					>
																						{cls}
																					</span>
																				))}
																			</div>
																		</td>
																	</tr>
																))}
															</tbody>
														</table>
													) : (
														<p className={styles.noSubjects}>
															No subjects assigned
														</p>
													)}
												</div>
											</td>
										</tr>
									)}
								</React.Fragment>
							))}
						</tbody>
					</table>
				) : (
					<div className={styles.emptyState}>
						<FontAwesomeIcon icon={faUser} className={styles.emptyIcon} />
						<h3>No Teachers Found</h3>
						<p>No teachers found in the system.</p>
					</div>
				)}
			</div>

			{/* Teacher Modal */}
			{isModalOpen && (
				<TeacherModal
					teacher={selectedTeacher}
					subjects={subjects}
					departments={departments}
					onClose={() => setIsModalOpen(false)}
				/>
			)}
		</div>
	);
};

export default TeacherManagement;
